import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, FileText, MapPin, Tag, Clock } from 'lucide-react';

const CATEGORIES = {
  financieel: 'Financieel & Belasting',
  contracten: 'Contracten & Huur',
  leveranciers: 'Leveranciers & Inkoop',
  techniek: 'Techniek & Apparatuur',
  voertuigen: 'Voertuigen',
  vastgoed: 'Vastgoed & Facilitair',
  veiligheid: 'Veiligheid & Compliance',
  milieu: 'Milieu & Infrastructuur',
  nieuwbouw: 'Nieuwbouw & Verbouwing',
  overnames: 'Overnames',
  automatisering: 'Automatisering',
};

export default function GlobalSearch({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { authAxios } = useAuth();
  const navigate = useNavigate();

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const api = authAxios();
      const res = await api.get(`/search?q=${encodeURIComponent(q)}&limit=15`);
      setResults(res.data);
      setSelectedIndex(0);
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => search(query), 200);
    return () => clearTimeout(timer);
  }, [query, open, search]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        navigate(`/kennisbank/${results[selectedIndex].entry_id}`);
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, results, selectedIndex, navigate, onOpenChange]);

  if (!open) return null;

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').substring(0, 120);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return ''; }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden" data-testid="global-search-panel">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input
            data-testid="global-search-input"
            autoFocus
            type="text"
            placeholder="Zoek in kennisbank..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 text-base text-slate-900 placeholder:text-slate-400 outline-none bg-transparent"
          />
          <kbd className="text-xs px-2 py-0.5 bg-slate-100 text-slate-400 rounded border border-slate-200">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              <div className="inline-block w-5 h-5 border-2 border-slate-200 border-t-[#2E75B6] rounded-full animate-spin" />
            </div>
          )}

          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              Geen resultaten voor "{query}"
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                Kennisbank ({results.length})
              </div>
              {results.map((entry, idx) => (
                <button
                  key={entry.entry_id}
                  onClick={() => {
                    navigate(`/kennisbank/${entry.entry_id}`);
                    onOpenChange(false);
                  }}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors duration-150 ${idx === selectedIndex ? 'bg-[#2E75B6]/5' : 'hover:bg-slate-50'}`}
                >
                  <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{entry.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{stripHtml(entry.content)}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="flex items-center gap-1 text-[10px] text-slate-400">
                        <MapPin className="w-3 h-3" />
                        {entry.location_id}
                      </span>
                      {entry.category && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full cat-${entry.category}`}>
                          {CATEGORIES[entry.category] || entry.category}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Clock className="w-3 h-3" />
                        {formatDate(entry.updated_at)}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && query.length < 2 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              Typ om te zoeken in titel, inhoud en tags
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
